// Grapheme-phase decodability engine — MA-259, the ONE decodability home (§00b).
//
// Direct port of the founder-approved reference grader
// (docs/build/specs/reading-decodable-audit-2026-07-16.audit.py), normative
// per ADR-070 D1. Grading rules (grapheme segmentation, sound-in-context,
// scheduled heart words, names-by-picture) are NOT reinvented here — every
// function below mirrors its Python counterpart 1:1 so the two stay provably
// in agreement (see src/curriculum/__tests__/decodabilityGoldSet.json, frozen
// from a real run of the reference script against this repo's own
// phonicsSequence.js/wordBanks.js).
//
// Plain JS (not .ts): imported unmodified from both TS (`allowJs`) and `.mjs`
// scripts, matching the existing phonicsSequence.js/wordBanks.js convention.
//
// ADR-070 OQ-1 (RESOLVED 2026-07-19; digraph microset added 2026-08-07):
// vowel-teams/r-controlled/split-e stay permanently OUTSIDE the modeled
// ladder. Digraphs are the ONE exception, resolved as a real MICROSETS row
// (R6: sh/ch/th) rather than a flag — a parallel cumulative dimension to the
// single-letter ladder (letters and digraphs are independent axes; R6 adds
// zero new letters). Vowel-teams/r-controlled/split-e remain untaught at
// every modeled phase, matching the reference script's original scope.

import { MICROSETS, lettersKnownAt, digraphsKnownAt } from "./phonicsSequence.js";
import { HFW_LIST } from "./wordBanks.js";
import { READING_MODULES } from "./readingModules.js";

export const PHASE_IDS = MICROSETS.map((m) => m.id);

const CUM_LETTERS = new Map(PHASE_IDS.map((id) => [id, new Set(lettersKnownAt(id))]));
const CUM_DIGRAPHS = new Map(PHASE_IDS.map((id) => [id, new Set(digraphsKnownAt(id))]));
// "NEW at phase P" = phonicsSequence.js's own per-microset letters array —
// each MICROSETS entry already IS the non-cumulative new-letters set.
const NEW_LETTERS = new Map(MICROSETS.map((m) => [m.id, new Set(m.letters)]));

// ---------------------------------------------------------------------------
// Heart-word authority (ADR-070 D6): HFW_LIST + hfwsForLevel, the ONE
// heart-word authority for the reading lane. Dolch/Fry are NOT consulted.
// ---------------------------------------------------------------------------

function heartsFirst(n) {
  return new Set(
    HFW_LIST.filter((e) => e.order <= n && e.type === "heart").map((e) => e.word.toLowerCase()),
  );
}

function allHeartWords() {
  return new Set(HFW_LIST.filter((e) => e.type === "heart").map((e) => e.word.toLowerCase()));
}

/** D6 schedule: R0-R2 -> hearts among first 8 (={a,I}); R3 -> first 30; R4+ -> all. */
function hfwsForLevel(phaseId) {
  if (phaseId === "R0" || phaseId === "R1" || phaseId === "R2") return heartsFirst(8);
  if (phaseId === "R3") return heartsFirst(30);
  return allHeartWords();
}

/** Non-modeled phonicsStage values (e.g. "R0_R1", "R7") mapped onto the
 * ladder MICROSETS actually models. R0_R1 is R1's own cumulative set (R1
 * already includes R0). "R6" is now a real modeled phase (digraphs, ADR-070
 * OQ-1) and returns itself via the PHASE_IDS.includes check above — it no
 * longer clamps. "R7" is a deliberate non-ladder scheduling sentinel (used
 * by reading.12/14/15 — silent-e/doubled-consonant/silent-letter content
 * that stays OQ-1-excluded from the modeled ladder) and clamps to R5,
 * matching the reference audit's original "R5-ceiling" treatment. */
function mapPhonicsStageToPhase(stage) {
  if (PHASE_IDS.includes(stage)) return stage;
  if (stage === "R0_R1") return "R1";
  return "R5";
}

/** Resolves a reading microSkill id (e.g. "ms.read.06") to its mapped R0-R5
 * phase, via the legacy-id crosswalk + READING_MODULES' own phonicsStage —
 * the ONE resolver (§00b), shared by decodabilityValidators.ts,
 * decodablePassageRelevel.mjs, and decodabilityEngineSelfTest.mjs so the
 * three never silently diverge. `legacyMap` is the loaded
 * legacySkillIdMap.json shape ({ microskill_to_legacy }); injected rather
 * than imported here so this stays a plain-JS module callable from both TS
 * (via getManifest) and a `.mjs` script (via a plain fs.readFileSync). */
export function resolvePhaseForTargetSkill(targetSkill, legacyMap) {
  const module = resolveReadingModuleForTargetSkill(targetSkill, legacyMap);
  const phonicsStage = module && module.config && module.config.phonicsStage;
  if (!phonicsStage) return null;
  return mapPhonicsStageToPhase(phonicsStage);
}

/** MA-260 — the RAW reading-module resolver underneath
 * resolvePhaseForTargetSkill: same legacy-id crosswalk, but returns the
 * READING_MODULES entry itself (or null) instead of the R5-clamped phase.
 * Exists because the serve-time phase gate needs two facts the clamped
 * phase erases: the module's UNclamped phonicsStage (an "R6"-stage skill
 * secured means the child is BEYOND the R0-R5 taught-letters ladder — the
 * gate must release them, not clamp them to R5) and the module's strand
 * (only `phonics_and_decoding` skills advance child.phase per ADR-070 D4 —
 * "advancement = securing the stage's DECODE skill"). Kept in this file so
 * the skill→module glue still has exactly ONE home (§00b); the existing
 * resolver above now composes on top of it, byte-identical behavior. */
export function resolveReadingModuleForTargetSkill(targetSkill, legacyMap) {
  if (!targetSkill) return null;
  const legacyIds = legacyMap.microskill_to_legacy[targetSkill];
  const legacyId = legacyIds && legacyIds.length ? legacyIds[0] : targetSkill;
  return READING_MODULES.find((m) => m.id === legacyId) ?? null;
}

// ---------------------------------------------------------------------------
// Normalization + segmentation
// ---------------------------------------------------------------------------

function normalize(tok) {
  let t = String(tok ?? "").normalize("NFKC").toLowerCase();
  t = t.replace(/['’`´]/g, "");
  t = t.replace(/[^a-z]/g, "");
  return t;
}

// Exported (MA-265) so decodeUtterance.ts's callout-sound table can be
// completeness-checked against this closed, already-enumerated set — a
// grapheme added here without a matching spoken-sound mapping would
// otherwise silently regress the tricky-part callout back to raw-letter
// TTS reading (the exact bug this closed).
export const MULTI_GRAPHEMES = [
  // 4
  "ough", "augh", "eigh",
  // 3
  "igh", "tch", "dge", "air", "ear", "ure", "que",
  // 2 consonant digraphs
  "sh", "ch", "th", "wh", "ph", "ck", "ng", "qu", "kn", "wr", "mb", "gn",
  // 2 vowel teams
  "ai", "ay", "ee", "ea", "oa", "ow", "ou", "oi", "oy", "oo", "au", "aw", "ew", "ue", "ie", "ei", "ey", "ui",
  // r-controlled
  "ar", "or", "er", "ir", "ur",
  // doubles
  "ll", "ss", "ff", "zz", "tt", "dd", "nn", "mm", "pp", "bb", "gg", "rr", "cc", "kk", "ww", "vv",
].sort((a, b) => b.length - a.length);

const CONSONANTS = new Set("bcdfghjklmnpqrstvwxyz".split(""));
const DOUBLES = new Set(
  MULTI_GRAPHEMES.filter((g) => g.length === 2 && g[0] === g[1] && CONSONANTS.has(g[0])),
);
const VOWELS = new Set("aeiou".split(""));
const VOICED = new Set([..."bdgjlmnrvwyz".split(""), "ng"]);
const INTERJECTIONS = new Set([
  "oh", "ah", "eh", "uh", "um", "hm", "hmm", "mm", "mmm", "aha", "shh", "psst", "huh",
]);
// MA-445: FINAL_S_ALLOW was too small (missed gus/sis, this item's own
// acceptance words — plus focus/tremendous, its "sweep" finding), but the
// fix is NOT "default to decodable and deny-list the voiced exceptions" —
// that was tried and reverted after it broke on the REAL corpus.
// `enumerateDecodeSupportStrings` (scripts/content/narratableStrings.mjs)
// runs gradeWord/buildDecodeUtterance over every distinct word in the
// ENTIRE ship-tier story corpus (~8,500 words), not a small curated list,
// so "default decodable" has to be right for every regular English plural/
// verb-agreement form in that corpus, not just the four words this file
// used to enumerate. It measurably is not: a regular inflectional -s on a
// vowel-final STEM that is an independently real word is reliably voiced
// (candles, tables, apples — the segment() split-e regex only fires on a
// word-final "e", so "candle"+"s" never re-detects the silent "e" the way
// "candle" alone does, and the bare "e" wrongly grades taught; jalebis —
// even a single open, non-team, non-"e" vowel stem plural like "jalebi"+
// "s" is voiced). Both were found by running the coverage gate over the
// real corpus, not guessed. Telling a "candles" or "jalebis" apart from a
// "gus" or "focus" requires knowing whether the stem is a real,
// independently-existing word — this file has no dictionary, and
// inventing a plausibility heuristic to fake one is exactly the
// anti-pattern this repo's own content-review discipline rejects
// elsewhere (MA-426: "do not synthesise a plausibility heuristic and
// present its output as review"). So the default STAYS "blocked" (voiced/
// untaught) — safe because it only ever under-decodes, never teaches a
// wrong sound-out — and the allowlist is grown by the same closed-set
// discipline VOICED/INTERJECTIONS/MULTI_GRAPHEMES already use in this
// file, but ONLY for this item's own acceptance words (gus, sis) plus the
// original yes/bus/gas/us. "focus" is deliberately NOT added even though
// the item's sweep names it: it is a real corpus decode-support target
// (public/stories), and adding it flips one already-narrated utterance
// from a whole-word replay to a freshly-segmented sound-out with no
// matching pregenerated audio — a real narration-coverage regression this
// item's own scope doesn't require paying for (a re-narration batch is
// MA-186/MA-390 territory, not a grading-bug fix). "tremendous" is inert
// either way — it is independently blocked by its untaught "ou" vowel-team
// grapheme regardless of this list — kept here anyway as a documented
// no-op, matching the item's finding without behavior change. "has" is
// deliberately NOT added — it stays undecodable per this item's own
// acceptance.
const FINAL_S_ALLOW = new Set(["yes", "bus", "gas", "us", "gus", "sis", "tremendous"]);

/** Longest-match grapheme segmentation. Split-e: final "e" after a single
 * consonant with a vowel before it becomes one "V_e" unit (beyond R5). */
export function segment(word) {
  const gs = [];
  let w = word;
  let splitEGrapheme = null;
  const m = /([aeiou])([bcdfghjklmnpqrstvwxyz])e$/.exec(w);
  if (m && w.length >= 3) {
    const vowelIdx = m.index;
    splitEGrapheme = m[1] + "_e";
    w = w.slice(0, vowelIdx) + "\0" + m[2];
  }
  let i = 0;
  while (i < w.length) {
    if (w[i] === "\0") {
      gs.push(splitEGrapheme);
      i += 1;
      continue;
    }
    let matched = null;
    for (const g of MULTI_GRAPHEMES) {
      if (w.startsWith(g, i)) {
        matched = g;
        break;
      }
    }
    if (matched) {
      gs.push(matched);
      i += matched.length;
    } else {
      gs.push(w[i]);
      i += 1;
    }
  }
  return gs;
}

// ---------------------------------------------------------------------------
// Grading
// ---------------------------------------------------------------------------

/** Is grapheme g (position idx of gs, in normalized word) taught at this
 * cumulative phase's letter set, sound-in-context? Direct port of
 * grapheme_taught() (audit.py L129-164) — every branch preserved 1:1,
 * EXCEPT the final -s-after-vowel branch (MA-445, see FINAL_S_ALLOW
 * above — grown, not inverted). */
function graphemeTaught(g, idx, gs, word, phaseLetters, phaseDigraphs) {
  if (g.includes("_e")) return false; // split digraph -> beyond the modeled ladder (A-DIG)
  if (DOUBLES.has(g)) return phaseLetters.has(g[0]); // A-DBL
  if (g === "ck") return phaseLetters.has("c") && phaseLetters.has("k"); // A-CK
  if (phaseDigraphs.has(g)) return true; // A-DIGRAPH: sh/ch/th taught once cumulative at R6+
  if (g.length > 1) return false; // A-DIG: all other multi-letter graphemes untaught
  if (!phaseLetters.has(g)) return false;
  const nxt = idx + 1 < gs.length ? gs[idx + 1] : null;
  const prv = idx > 0 ? gs[idx - 1] : null;
  if (g === "c" && nxt && "eiy".includes(nxt[0])) return false; // c=/s/
  if (g === "g" && nxt && "eiy".includes(nxt[0])) return false; // g=/j/ risk — conservative
  if (g === "y" && idx !== 0) return false; // y-as-vowel
  if (g === "s" && idx === gs.length - 1 && prv) {
    const base = !prv.includes("_e") ? prv[prv.length - 1] : prv[0];
    if (VOICED.has(base) && !VOWELS.has(base)) return false; // -s after voiced consonant = /z/
    if (VOWELS.has(prv[0]) && !FINAL_S_ALLOW.has(word)) return false; // -s after vowel = /z/
  }
  if (VOWELS.has(g)) {
    if (idx === gs.length - 1) return false; // open/final vowel -> long or schwa
    if (g === "a") {
      if (idx === 0 && gs.length >= 3 && gs.slice(2).some((x) => VOWELS.has(x[0]) || x.includes("_e"))) {
        return false; // schwa heuristic: word-initial a + C + more (away/about)
      }
    }
    if (nxt && (VOWELS.has(nxt[0]) || nxt.includes("_e"))) return false; // vowel before vowel — unmatched team
  }
  return true;
}

/** A-ED: ends consonant+'ed' with an earlier vowel = inflectional -ed. */
function edInflection(word) {
  if (word.length >= 4 && word.endsWith("ed") && !"aeiou".includes(word[word.length - 3])) {
    if ([...word.slice(0, -3)].some((c) => "aeiou".includes(c))) return true;
  }
  return false;
}

/** Grade a single (already-normalized) word at a cumulative phase.
 * @returns {{verdict: "decodable"|"heart"|"undecodable", detail: string}} */
export function gradeWord(word, phaseId) {
  const letters = CUM_LETTERS.get(phaseId);
  const digraphs = CUM_DIGRAPHS.get(phaseId);
  if (!letters) throw new Error(`gradeWord: unknown phase "${phaseId}"`);
  if (hfwsForLevel(phaseId).has(word)) return { verdict: "heart", detail: "scheduled heart word" };
  if (allHeartWords().has(word)) {
    return { verdict: "undecodable", detail: "heart word not yet scheduled at this phase" };
  }
  if (INTERJECTIONS.has(word)) return { verdict: "undecodable", detail: "irregular interjection" };
  const gs = segment(word);
  if (edInflection(word)) return { verdict: "undecodable", detail: "-ed inflection" };
  const bad = gs
    .map((g, i) => ({ g, i }))
    .filter(({ g, i }) => !graphemeTaught(g, i, gs, word, letters, digraphs))
    .map(({ g }) => g);
  if (bad.length) return { verdict: "undecodable", detail: `untaught: ${bad.join(",")}` };
  return { verdict: "decodable", detail: "" };
}

/** Does this word use a grapheme that's brand-new (not cumulative) at this
 * phase? Reported metric only — per ADR-070 D7, the cumulative "phase_passed"
 * used for serving is NOT gated on this; it is the separate strict phase-
 * focus share used for audit/generation targeting (MA-263), never the serve
 * gate (a text can be servable at R2 and still be "nominal"). */
function usesNewGpc(word, phaseId) {
  const newAtPhase = NEW_LETTERS.get(phaseId);
  if (!newAtPhase) throw new Error(`usesNewGpc: unknown phase "${phaseId}"`);
  return segment(word).some((g) => g.length === 1 && newAtPhase.has(g));
}

// ---------------------------------------------------------------------------
// Thresholds (ADR-070 D5, PROVISIONAL)
// ---------------------------------------------------------------------------

const BASE_THRESH = {
  R0: { minDec: 0.95, maxHeart: 0.05, maxRoguePerPage: 1 },
  R1: { minDec: 0.95, maxHeart: 0.05, maxRoguePerPage: 1 },
  R2: { minDec: 0.95, maxHeart: 0.05, maxRoguePerPage: 1 },
  R3: { minDec: 0.90, maxHeart: 0.08, maxRoguePerPage: 2 },
  R4: { minDec: 0.90, maxHeart: 0.08, maxRoguePerPage: 2 },
  R5: { minDec: 0.90, maxHeart: 0.08, maxRoguePerPage: 2 },
  // PROVISIONAL (ADR-070 D5 precedent): inherits R5's tier — no digraph
  // story content exists yet to calibrate against (this slice is
  // deliberately not bundled with a content batch).
  R6: { minDec: 0.90, maxHeart: 0.08, maxRoguePerPage: 2 },
};

/** D5 amendment: at R0-R2, heart budget passes if EITHER the flat 5% cut OR
 * (heartCount <= 2 AND tokenCount <= 40) — the article "a" in a 22-token text
 * (9.1% heart) would otherwise fail an otherwise-100%-decodable text. */
function heartBudgetOk(phaseId, heartCount, heartRatio, tokenCount) {
  const { maxHeart } = BASE_THRESH[phaseId];
  if (heartRatio <= maxHeart) return true;
  if ((phaseId === "R0" || phaseId === "R1" || phaseId === "R2") && heartCount <= 2 && tokenCount <= 40) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Whole-text / whole-story grading
// ---------------------------------------------------------------------------

function tokensFromText(text) {
  return (text || "").match(/[A-Za-z][A-Za-z’']*/g) || [];
}

/** Names taught-by-picture (A-NAME): any token capitalized mid-sentence
 * anywhere in the story; ALL occurrences (incl. sentence-initial) of that
 * token then count as the name. Direct port of detect_names_from_text. */
function detectNamesFromText(pages) {
  const names = new Set();
  for (const text of pages) {
    const re = /[A-Z][a-z’']+/g;
    let m;
    while ((m = re.exec(text || ""))) {
      const j = m.index;
      const prior = (text.slice(0, j) || "").replace(/\s+$/, "");
      if (!prior) continue;
      const last = prior[prior.length - 1];
      if (!'.!?"“”‘’«»…'.includes(last) && !"-–—(".includes(last)) {
        names.add(normalize(m[0]));
      }
    }
  }
  return names;
}

/** Grade a story (or passage) shaped as `{ pages: [{ text }] }` across every
 * cumulative phase. Direct port of audit_story (audit.py L228-277). */
export function auditStory(story) {
  const rawPages = (story.pages || []).map((p) => tokensFromText(p && p.text));
  const rawTexts = (story.pages || []).map((p) => (p && p.text) || "");
  const names = detectNamesFromText(rawTexts);

  const pageNorm = rawPages.map((toks) => toks.map(normalize).filter(Boolean));
  const allNorm = pageNorm.flat();
  const gradable = allNorm.filter((t) => !names.has(t));
  const types = new Set(gradable);

  const perPhase = {};
  for (const phase of PHASE_IDS) {
    let dec = 0;
    let heart = 0;
    const rogue = new Map();
    const pageRogueCounts = [];
    for (const row of pageNorm) {
      let pageRogue = 0;
      for (const t of row) {
        if (names.has(t)) continue;
        const { verdict } = gradeWord(t, phase);
        if (verdict === "decodable") dec += 1;
        else if (verdict === "heart") heart += 1;
        else {
          rogue.set(t, (rogue.get(t) || 0) + 1);
          pageRogue += 1;
        }
      }
      pageRogueCounts.push(pageRogue);
    }
    const n = gradable.length || 1;
    const decRatio = dec / n;
    const heartRatio = heart / n;
    const maxPageRogue = pageRogueCounts.length ? Math.max(...pageRogueCounts) : 0;
    const { minDec, maxRoguePerPage } = BASE_THRESH[phase];
    const heartCount = heart;
    const passes =
      decRatio >= minDec &&
      heartBudgetOk(phase, heartCount, heartRatio, gradable.length) &&
      maxPageRogue <= maxRoguePerPage;
    perPhase[phase] = {
      dec,
      heart,
      rogue: [...rogue.values()].reduce((a, b) => a + b, 0),
      decRatio,
      heartRatio,
      maxPageRogue,
      passes,
    };
  }
  const passedPhases = PHASE_IDS.filter((p) => perPhase[p].passes);
  const phasePassed = passedPhases.length ? passedPhases[0] : null;
  const newGpcShare = Object.fromEntries(
    PHASE_IDS.map((p) => [p, gradable.length ? gradable.filter((t) => usesNewGpc(t, p)).length / gradable.length : 0]),
  );
  // R4 (audit.py's own scope note): names are "listed + flagged" — excluded
  // from rogue counts/denominator above, but a name the engine's own R5-
  // ceiling rule would itself grade undecodable is worth surfacing (its
  // "taught by picture" fallback is load-bearing, not merely a formality).
  // Direct port of audit_story's flagged_names (audit.py L273), previously
  // dropped from this JS return entirely (MA-445).
  const flaggedNames = [...names].filter((nm) => gradeWord(nm, "R5").verdict === "undecodable").sort();

  return {
    tokens: gradable.length,
    types: types.size,
    names: [...names].sort(),
    flaggedNames,
    perPhase,
    phasePassed,
    newGpcShare,
  };
}
